import { useCallback, useEffect, useState } from 'react'
import type { CartRequestCreator } from './creatorBusiness'

// 견적 계산기 카트 — POST /cart 요청 바디(creators)를 보관.
// 원본(admin)은 zustand+sessionStorage. 포털엔 zustand가 없어 useState + sessionStorage로.
// sessionStorage라 모달 닫기/페이지 이동엔 유지, 탭 닫으면 초기화.
const STORAGE_KEY = 'creator-business-cart'

function read(): CartRequestCreator[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as CartRequestCreator[]) : []
  } catch {
    return []
  }
}

export interface CreatorCart {
  creators: CartRequestCreator[]
  /** 새 크리에이터만 누적 추가 (기존 항목 보존) */
  addCreators: (items: CartRequestCreator[]) => void
  removeCreator: (salesforceCreatorId: string) => void
  /** 특정 크리에이터의 단가 항목 key 목록 교체 */
  setProductItems: (salesforceCreatorId: string, keys: string[]) => void
  clear: () => void
}

export function useCreatorCart(): CreatorCart {
  const [creators, setCreators] = useState<CartRequestCreator[]>(read)

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(creators))
    } catch {
      /* ignore quota */
    }
  }, [creators])

  const addCreators = useCallback((items: CartRequestCreator[]) => {
    setCreators((prev) => {
      const map = new Map(prev.map((c) => [c.salesforceCreatorId, c]))
      for (const it of items) if (!map.has(it.salesforceCreatorId)) map.set(it.salesforceCreatorId, it)
      return [...map.values()]
    })
  }, [])

  const removeCreator = useCallback((id: string) => {
    setCreators((prev) => prev.filter((c) => c.salesforceCreatorId !== id))
  }, [])

  const setProductItems = useCallback((id: string, keys: string[]) => {
    setCreators((prev) =>
      prev.map((c) =>
        c.salesforceCreatorId === id ? { ...c, productItems: keys.map((k) => ({ key: k })) } : c,
      ),
    )
  }, [])

  const clear = useCallback(() => setCreators([]), [])

  return { creators, addCreators, removeCreator, setProductItems, clear }
}
